/* Documents — the shared file list.
   No mockup for this screen; the shape is spec/03 archetype A, built from the shared list chrome
   (`component/list-page.scss`) and the shared cell primitives, so it reads as the same product as
   /visit and /supervisors.

   The filter card is the shared one — `lp-filters`, `<Form layout="vertical" component={false}>`,
   `FIELD_COL` tracks, the `FilterChips` row and the `lp-filters__foot` with Reset — even though
   `GET documents/` offers exactly one narrowing (search). One reduction against the archetype:
   **no segments and no quality chips**, since a document has no lifecycle state to split on and
   nothing derived to call out.

   A document has no detail route, so the row is not clickable at all: opening the file is an
   explicit per-row button (`ExportOutlined`, new tab), since leaving the app should be aimed at
   rather than a side effect of clicking a row.

   Functionality is unchanged from the pre-redesign page: search, sort by name or date, paging,
   upload (name + file), download, delete. Delete now confirms first, per spec/03. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Col, Empty, Flex, Form, Input, Modal, Row, Table, Tag, Tooltip, Typography, Upload } from "antd";
import {
  CloudUploadOutlined,
  DeleteOutlined,
  ExportOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import FilterChips from "../../component/FilterChips";
import { RowActions, TableFoot, actionsColumn, dash, useTableScrollY } from "../../component/table-cells";
import { BI_REPORTS, DOCUMENTS, ENTITY_DROPDOWN_SETTINGS, PHOTO_REPORT, POINTS_ADMIN, SIDE_NAV, TRANSLATION_DOWNLOADER, VISIT_PAGE } from "../../config/lang";
import { call, callForm, getAbortable } from "../../util/action";
import { fill, translate } from "../../util/util";
import { dateTimeCreated } from "../../util/formatter";

const PAGE_SIZES = [25, 50, 100];

/* Column key → the field the API orders on. A key missing here is not sortable. */
const ORDERING = { title: "title", created: "created" };

/* The same field track as every other filter card (spec/02): a third of the row from xl down. */
const FIELD_COL = { xs: { flex: "0 0 100%" }, md: { flex: "0 0 50%" }, xl: { flex: "0 0 33.3333%" } };

const DEFAULT_SORT = { key: "created", dir: "descend" };

const sortKey = sort => (sort ? `${sort.dir === "descend" ? "-" : ""}${sort.key}` : "");

/* URL ↔ state, the same round trip the other list pages do: a filtered view is shareable, and
   `history.replace` keeps filtering out of the history stack. */
function stateToSearch({ search, page, size, sort }) {
  const p = new URLSearchParams();
  if (search) p.set("q", search);
  if (page > 1) p.set("page", String(page));
  if (size !== PAGE_SIZES[0]) p.set("size", String(size));
  if (sortKey(sort) !== sortKey(DEFAULT_SORT)) p.set("sort", sortKey(sort));
  return p.toString();
}

function searchToState(locationSearch) {
  const p = new URLSearchParams(locationSearch);
  const sort = p.get("sort");
  return {
    search: p.get("q") || "",
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(Number(p.get("size"))) ? Number(p.get("size")) : PAGE_SIZES[0],
    sort: sort === null ? DEFAULT_SORT : ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

const FILE_ICON = {
  pdf: FilePdfOutlined,
  doc: FileWordOutlined,
  docx: FileWordOutlined,
  xls: FileExcelOutlined,
  xlsx: FileExcelOutlined,
  csv: FileExcelOutlined,
  png: FileImageOutlined,
  jpg: FileImageOutlined,
  jpeg: FileImageOutlined,
  gif: FileImageOutlined,
  svg: FileImageOutlined,
  webp: FileImageOutlined,
  zip: FileZipOutlined,
  rar: FileZipOutlined,
  txt: FileTextOutlined,
};

const extensionOf = url => {
  if (!url) return "";
  const name = url
    .split("/")
    .pop()
    .split("?")[0];
  return name.includes(".")
    ? name
        .split(".")
        .pop()
        .toLowerCase()
    : "";
};

const View = () => {
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(DOCUMENTS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);
  const ta = key => translate(POINTS_ADMIN, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);
  const tdl = key => translate(TRANSLATION_DOWNLOADER, key, lang);
  const te = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tb = key => translate(BI_REPORTS, key, lang);

  /* Read once. After mount this component owns the state and writes the URL, not the reverse. */
  const initial = useRef(searchToState(location.search)).current;

  /* Typing is separate from the applied term: the field must not lag the keystroke while the
     request it will eventually cause is still debounced. */
  const [typed, setTyped] = useState(initial.search);
  const [search, setSearch] = useState(initial.search);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [sort, setSort] = useState(initial.sort);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("page_size", String(size));
    if (search) p.set("search", search);
    if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);
    return p.toString();
  }, [search, page, size, sort]);

  const [reloads, setReloads] = useState(0);
  const reload = () => setReloads(n => n + 1);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`documents/?${query}`, controller.signal)
      .then(response => {
        if (!response) return;
        setRows(response.results || []);
        setTotal(response.count || 0);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setRows([]);
        setTotal(0);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query, reloads]);

  /* The URL trails the state; `replace` so filtering does not fill the history stack. */
  useEffect(() => {
    const next = stateToSearch({ search, page, size, sort });
    if (next !== location.search.replace(/^\?/, "")) history.replace({ search: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, size, sort]);

  /* 250ms, matching every other search box in the app: one request per pause, not per keystroke. */
  useEffect(() => {
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(typed);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, search]);

  /* No `useLabelCache` here: the one filter is free text, so a chip can always label itself. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chips = useMemo(() => (search ? [{ key: "q", label: t("search"), value: search }] : []), [search, lang]);

  const clearSearch = () => {
    setTyped("");
    setSearch("");
    setPage(1);
  };

  const clearChip = key => key === "q" && clearSearch();

  const resetFilters = () => {
    clearSearch();
    setSort(DEFAULT_SORT);
  };

  const scrollY = useTableScrollY();

  const openFile = row => row.document && window.open(row.document, "_blank", "noopener");

  const remove = row =>
    Modal.confirm({
      title: fill(t("confirm_delete"), { name: row.title }),
      okText: ta("delete_btn"),
      okButtonProps: { danger: true },
      /* `Modal.confirm` is a static call: it renders outside the tree and does not inherit
         `AppShell`'s `ConfigProvider locale`, so the buttons need explicit labels. */
      cancelText: tdl("cancel"),
      onOk: () =>
        new Promise(resolve => {
          call({ method: "DELETE", url: `documents/${row.id}/` }, () => {
            Alert.success(t("file_deleted"), { position: "top-right" });
            /* Refetch rather than splice: the row leaving changes the page's contents, and the
               server owns which record moves up into it. */
            reload();
            resolve();
          });
        }),
    });

  const submit = values => {
    const picked = values.file[0];
    const fd = new FormData();
    fd.append("document", picked.originFileObj || picked);
    fd.append("title", values.title);

    setSaving(true);
    callForm({ method: "POST", url: "documents/", post: fd }, response => {
      setSaving(false);
      if (!response?.id) {
        Alert.error(ta("save_error"), { position: "top-right" });
        return;
      }
      Alert.success(t("file_successfully_uploaded"), { position: "top-right" });
      setOpen(false);
      form.resetFields();
      setPage(1);
      reload();
    });
  };

  const columns = useMemo(
    () => [
      {
        key: "title",
        title: t("file_name"),
        width: 320,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        sortOrder: sort?.key === "title" ? sort.dir : null,
        render: (value, row) => {
          const Icon = FILE_ICON[extensionOf(row.document)] || FileOutlined;
          return (
            <Flex align="center" gap={8} className="lp-main">
              <Typography.Text type="secondary">
                <Icon style={{ fontSize: 18 }} />
              </Typography.Text>
              <Typography.Text strong ellipsis={{ tooltip: row.title }}>
                {row.title || dash}
              </Typography.Text>
            </Flex>
          );
        },
      },
      {
        key: "type",
        title: t("f_type"),
        width: 110,
        render: (value, row) => {
          const ext = extensionOf(row.document);
          return ext ? <Tag>{ext.toUpperCase()}</Tag> : dash;
        },
      },
      {
        key: "created",
        title: t("creation_time"),
        width: 200,
        sorter: true,
        sortOrder: sort?.key === "created" ? sort.dir : null,
        render: (value, row) => <span className="lp-num">{dateTimeCreated(row)}</span>,
      },
      {
        /* Opening the file is an explicit control, not a row click — the row is not a navigation,
           and a click that leaves the app should have to be aimed at. The menu keeps the row's
           remaining action so the affordance stays where it is on every other table. */
        ...actionsColumn((value, row) => (
          <Flex align="center" justify="flex-end" gap={4}>
            <Tooltip title={tb("open_new_tab")}>
              <Button
                type="text"
                size="small"
                icon={<ExportOutlined />}
                aria-label={tb("open_new_tab")}
                disabled={!row.document}
                onClick={() => openFile(row)}
              />
            </Tooltip>
            <RowActions
              label={tv("row_actions")}
              items={[{ key: "delete", icon: <DeleteOutlined />, label: ta("delete_btn"), danger: true }]}
              onClick={() => remove(row)}
            />
          </Flex>
        )),
        width: 92,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, sort]
  );

  return (
    <Flex vertical gap="middle" className="shell-content lp">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {translate(SIDE_NAV, "documents", lang)}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
        <Button type="primary" icon={<UploadOutlined />} onClick={() => setOpen(true)}>
          {t("add_file")}
        </Button>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        {/* `component={false}`: there is nothing to submit — filters apply as they change, and a
            real <form> would turn Enter in the search box into a page reload. */}
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...FIELD_COL}>
              {/* With no `name` antd renders the label without `htmlFor`, so the field carries its
                  own accessible name (spec/03#accessibility-floors). */}
              <Form.Item label={t("search")}>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t("search_ph")}
                  aria-label={t("search")}
                  value={typed}
                  onChange={event => setTyped(event.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {te("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {translate(SIDE_NAV, "documents", lang)}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 700, y: scrollY }}
          /* Paging is the footer below, so with `pagination={false}` the only thing left for the
             table's own onChange is the sorter. */
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            /* "nothing here yet" and "no results for this search" are different states and get
               different copy (spec/03). */
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical gap={4}>
                    <Typography.Text strong>{search ? t("empty_search_title") : t("empty_title")}</Typography.Text>
                    <Typography.Text type="secondary">{search ? t("empty_search_desc") : t("empty_desc")}</Typography.Text>
                  </Flex>
                }
              />
            ),
          }}
        />

        <TableFoot
          id="doc"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: tv("rows_per_page"), range: tv("range_of") }}
          onPage={setPage}
          onSize={next => {
            setSize(next);
            setPage(1);
          }}
        />
      </Card>

      <Modal
        open={open}
        title={t("add_file")}
        okText={t("add_file")}
        cancelText={tdl("cancel")}
        confirmLoading={saving}
        onOk={() => form.submit()}
        onCancel={() => setOpen(false)}
        destroyOnHidden>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} preserve={false}>
          <Form.Item name="title" label={t("file_name")} rules={[{ required: true, message: t("file_name_cannot_be_empty") }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            name="file"
            valuePropName="fileList"
            /* `beforeUpload: false` keeps the file local, so the event carries the picked list. */
            getValueFromEvent={event => event?.fileList}
            /* An explicit validator, not `required`: async-validator treats an unspecified rule as
               a string, so an empty `fileList` passes `required` and `values.file[0]` then throws
               on submit. */
            rules={[{ validator: (rule, value) => (value && value.length ? Promise.resolve() : Promise.reject(new Error(t("need_to_add_file")))) }]}>
            <Upload.Dragger beforeUpload={() => false} maxCount={1}>
              <Typography.Text type="secondary">
                <CloudUploadOutlined style={{ fontSize: 28 }} />
              </Typography.Text>
              <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                {t("click_or_drag_files_here")}
              </Typography.Paragraph>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

export default View;
